import React, { useState } from 'react';
import { Checkbox, Drawer, Icon, Pills, Text, Tooltip, useToast } from 'barte-design-system';
import { CaretDown, CaretUp, Info, Prohibit, TrendDown } from '@phosphor-icons/react';
import './DrawerPendencias.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type TipoPendencia = 'Taxas padrão' | 'Markup zerado' | 'Markup negativo';

interface PendenciaItem {
  id: string;
  tipo: TipoPendencia;
  valorTaxa: string;
  metodo: string;
  parcela: string;
  bandeira: string;
  captura: string;
}

export interface PlanoPendencias {
  nome: string;
  planoId: string;
}

interface DrawerPendenciasProps {
  isOpen: boolean;
  onClose: () => void;
  plano: PlanoPendencias | null;
}

// ─── Mock helpers ─────────────────────────────────────────────────────────────

const TIPO_PILL: Record<TipoPendencia, 'info' | 'warning' | 'error'> = {
  'Taxas padrão':    'info',
  'Markup zerado':   'warning',
  'Markup negativo': 'error',
};

const MOCK_SELLERS_POR_PLANO: Record<string, string[]> = {
  '#00612': ['Loja Central LTDA', 'Mercado Bom Preço', 'Padaria Pão Quente'],
  '#00455': ['Tech Store', 'FitLife Academia', 'Ótica Visão Clara', 'Pet Shop Animal Love', 'Barbearia Corte Certo', 'Lavanderia Express', 'Farmácia Saúde & Vida', 'Livraria Cultura', 'Sorveteria Gelato'],
  '#00456': ['Restaurante Sabor Bom', 'Posto Combustível Star', 'Hortifruti Verde Vida'],
  '#00204': ['Supermercado Família', 'Auto Peças Brasil', 'Consultório Dr. Lima', 'TechStore Informática', 'Norte Distribuidora'],
};

function getMockSellers(planoId: string): string[] {
  return MOCK_SELLERS_POR_PLANO[planoId] ?? ['Seller Alpha', 'Seller Beta', 'Seller Gamma', 'Seller Delta'];
}

const TIPO_ICON: Record<TipoPendencia, any> = {
  'Taxas padrão':    Info,
  'Markup zerado':   Prohibit,
  'Markup negativo': TrendDown,
};

const TIPO_TOOLTIP: Record<TipoPendencia, string> = {
  'Taxas padrão':    'Este plano ainda está usando as taxas de contrato, sem taxas personalizadas.',
  'Markup zerado':   'Seller operando sem margem em algumas transações.',
  'Markup negativo': 'Markup negativo faz com que o seller gere prejuízo nas transações.',
};

const VALORES_TAXA = ['1,99%', '2,49%', '2,99%', '3,49%', '0,99%', '1,49%', '4,00%', '3,99%'];
const METODOS = ['Cartão de crédito', 'Débito', 'PIX', 'Boleto', 'Link de Pagamento'];
const BANDEIRAS = ['Mastercard', 'Visa', 'Elo', 'Amex', 'Hipercard'];
const CAPTURAS = ['Link de Pagamento', 'Maquininha', 'TEF', 'E-commerce', 'App'];
const PARCELAS = [
  'a partir da 1ª parcela',
  'a partir da 3ª parcela',
  'a partir da 7ª parcela',
  'a partir da 12ª parcela',
  'parcela única',
];
const TIPOS: TipoPendencia[] = ['Taxas padrão', 'Markup zerado', 'Markup negativo'];

function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return Math.abs(s) / 0xffffffff;
  };
}

function getMockPendencias(planoId: string): PendenciaItem[] {
  const seed = planoId.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const rand = seededRandom(seed);
  const count = 5 + Math.floor(rand() * 6); // 5–10 itens

  return Array.from({ length: count }, (_, i) => ({
    id:         `${planoId}-P${i + 1}`,
    tipo:       TIPOS[Math.floor(rand() * TIPOS.length)],
    valorTaxa:  VALORES_TAXA[Math.floor(rand() * VALORES_TAXA.length)],
    metodo:     METODOS[Math.floor(rand() * METODOS.length)],
    parcela:    PARCELAS[Math.floor(rand() * PARCELAS.length)],
    bandeira:   BANDEIRAS[Math.floor(rand() * BANDEIRAS.length)],
    captura:    CAPTURAS[Math.floor(rand() * CAPTURAS.length)],
  }));
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DrawerPendencias({ isOpen, onClose, plano }: DrawerPendenciasProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [sellersExpanded, setSellersExpanded] = useState(false);
  const toast = useToast();

  // Reset selection when drawer opens for a new plano
  React.useEffect(() => {
    if (isOpen) setSelected([]);
  }, [isOpen, plano?.planoId]);

  if (!plano) return null;

  const pendencias = getMockPendencias(plano.planoId);
  const sellers = getMockSellers(plano.planoId);

  function toggleItem(id: string, checked: boolean) {
    setSelected((prev) =>
      checked ? [...prev, id] : prev.filter((s) => s !== id),
    );
  }

  const count = selected.length;
  const primaryLabel = count > 0
    ? `Consentir com pendências (${count})`
    : 'Consentir com pendências';

  return (
    <Drawer
      isOpen={isOpen}
      title="Pendências"
      onClose={onClose}
      footer={{
        primaryButton: {
          children: primaryLabel,
          disabled: count === 0,
          onClick: () => {
            toast.success({ title: `Feito. Menos ${count} pendência${count !== 1 ? 's' : ''}!` });
            onClose();
          },
        },
        secondaryButton: {
          children: 'Cancelar',
          onClick: onClose,
        },
      }}
    >
      <div className="dpend-body">

        {/* ── Cabeçalho do plano ──────────────────────────────────────── */}
        <div className="dpend-plano-header">
          <Text variant="label-base">{plano.nome}</Text>
          <button className="dpend-ver-plano">Corrigir pendências</button>
        </div>

        {/* ── Sellers aplicados ───────────────────────────────────────── */}
        <div className="dpend-sellers-box">
          <div className="dpend-sellers-toggle" onClick={() => setSellersExpanded((v) => !v)}>
            <Text variant="body-sm">{sellers.length} seller{sellers.length !== 1 ? 's' : ''} impactado{sellers.length !== 1 ? 's' : ''}</Text>
            <Icon icon={sellersExpanded ? CaretUp : CaretDown} size={16} weight="bold" />
          </div>
          {sellersExpanded && (
            <div className="dpend-sellers-list">
              {sellers.map((s) => (
                <Text key={s} variant="body-xs" className="dpend-seller-name">{s}</Text>
              ))}
            </div>
          )}
        </div>

        {/* ── Lista de pendências ─────────────────────────────────────── */}
        <div className="dpend-list-section">
          <Text variant="label-base">{pendencias.length} pendência{pendencias.length !== 1 ? 's' : ''}</Text>
        </div>
        <div className="dpend-list">
          {pendencias.map((item) => (
            <div key={item.id} className={`dpend-card${selected.includes(item.id) ? ' dpend-card--selected' : ''}`}>
              <div className="dpend-row">
                <Text variant="body-sm" className="dpend-label">Tipo de pendência</Text>
                <Tooltip label={TIPO_TOOLTIP[item.tipo]} placement="left">
                  <span>
                    <Pills
                      label={item.tipo}
                      state={TIPO_PILL[item.tipo]}
                      size="sm"
                      icon={TIPO_ICON[item.tipo]}
                    />
                  </span>
                </Tooltip>
              </div>
              <div className="dpend-row">
                <Text variant="body-sm" className="dpend-label">Valor da taxa</Text>
                <Text variant="label-sm">{item.valorTaxa}</Text>
              </div>
              <div className="dpend-row">
                <Text variant="body-sm" className="dpend-label">Método</Text>
                <Text variant="label-sm">{item.metodo}</Text>
              </div>
              <div className="dpend-row">
                <Text variant="body-sm" className="dpend-label">Parcela</Text>
                <Text variant="label-sm">{item.parcela}</Text>
              </div>
              <div className="dpend-row">
                <Text variant="body-sm" className="dpend-label">Bandeira</Text>
                <Text variant="label-sm">{item.bandeira}</Text>
              </div>
              <div className="dpend-row">
                <Text variant="body-sm" className="dpend-label">Captura</Text>
                <Text variant="label-sm">{item.captura}</Text>
              </div>
              <div
                className="dpend-row dpend-consent-row"
                onClick={() => toggleItem(item.id, !selected.includes(item.id))}
              >
                <Text variant="label-sm">Consentir com pendência</Text>
                <Checkbox
                  checked={selected.includes(item.id)}
                  onChange={(checked) => toggleItem(item.id, checked)}
                  size="sm"
                />
              </div>
            </div>
          ))}
        </div>

      </div>
    </Drawer>
  );
}
